<template>
  <div class="menu-page">
    <header class="menu-page__header">
      <div>
        <h1>{{ $t('catalog.menu.title') }}</h1>
        <p>{{ $t('catalog.menu.subtitle') }}</p>
      </div>
    </header>

    <div class="menu-page__grid">
      <section class="menu-card">
        <div class="menu-card__icon">
          <i class="mdi mdi-download-outline"></i>
        </div>
        <h2>{{ $t('catalog.menu.exportTitle') }}</h2>
        <p>{{ $t('catalog.menu.exportHint') }}</p>
        <button
          type="button"
          class="menu-card__btn"
          :disabled="exporting"
          @click="exportMenu"
        >
          <i class="mdi" :class="exporting ? 'mdi-loading mdi-spin' : 'mdi-file-download-outline'"></i>
          {{ exporting ? $t('catalog.menu.exporting') : $t('catalog.menu.exportBtn') }}
        </button>
      </section>

      <section class="menu-card">
        <div class="menu-card__icon menu-card__icon--import">
          <i class="mdi mdi-upload-outline"></i>
        </div>
        <h2>{{ $t('catalog.menu.importTitle') }}</h2>
        <p>{{ $t('catalog.menu.importHint') }}</p>

        <label class="menu-card__file">
          <input
            ref="fileInput"
            type="file"
            accept="application/json,.json"
            :disabled="importing"
            @change="onFileChange"
          >
          <span>
            <i class="mdi mdi-json"></i>
            {{ selectedFileName || $t('catalog.menu.chooseFile') }}
          </span>
        </label>

        <button
          type="button"
          class="menu-card__btn menu-card__btn--primary"
          :disabled="importing || !selectedFile"
          @click="importMenu"
        >
          <i class="mdi" :class="importing ? 'mdi-loading mdi-spin' : 'mdi-database-import-outline'"></i>
          {{ importing ? $t('catalog.menu.importing') : $t('catalog.menu.importBtn') }}
        </button>

        <div v-if="importResult" class="menu-card__result">
          <p>{{ $t('catalog.menu.resultTitle') }}</p>
          <ul>
            <li>{{ $t('catalog.menu.resultCategories', { count: importResult.created_categories }) }}</li>
            <li>{{ $t('catalog.menu.resultCreated', { count: importResult.created_products }) }}</li>
            <li>{{ $t('catalog.menu.resultUpdated', { count: importResult.updated_products }) }}</li>
          </ul>
        </div>
      </section>
    </div>

    <section class="menu-page__example">
      <h3>{{ $t('catalog.menu.formatTitle') }}</h3>
      <pre>{{ exampleJson }}</pre>
    </section>
  </div>
</template>

<script>
import { exportCatalogMenu, importCatalogMenu } from '@/api/catalogMenu'
import { notify, notifyError } from '@/utils/notify'

export default {
  name: 'CatalogMenuPage',
  data() {
    return {
      exporting: false,
      importing: false,
      selectedFile: null,
      selectedFileName: '',
      importResult: null,
      exampleJson: `{
  "brand": "SOGYM",
  "category": "HALAL ET ORTALYGY",
  "categories": [
    {
      "name": "Полуфабрикаты",
      "products": [
        { "name": "Люля кебаб", "price": 5500, "unit": "кг" },
        { "name": "Жайма", "price": 1000, "unit": "шт" }
      ]
    }
  ]
}`,
    }
  },
  methods: {
    onFileChange(event) {
      const file = event.target.files && event.target.files[0]
      this.selectedFile = file || null
      this.selectedFileName = file ? file.name : ''
      this.importResult = null
    },
    async exportMenu() {
      this.exporting = true
      try {
        const data = await exportCatalogMenu()
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        const brand = (data && data.brand) ? String(data.brand).replace(/\s+/g, '_').toLowerCase() : 'menu'
        link.href = url
        link.download = `${brand}_menu.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        notify(this.$t('catalog.menu.exportDone'))
      } catch (error) {
        notifyError(error, this.$t('catalog.menu.exportFailed'))
      } finally {
        this.exporting = false
      }
    },
    async importMenu() {
      if (!this.selectedFile) return
      this.importing = true
      this.importResult = null
      try {
        const formData = new FormData()
        formData.append('file', this.selectedFile)
        const result = await importCatalogMenu(formData)
        this.importResult = result
        notify(result.message || this.$t('catalog.menu.importDone'))
        if (this.$refs.fileInput) this.$refs.fileInput.value = ''
        this.selectedFile = null
        this.selectedFileName = ''
      } catch (error) {
        notifyError(error, this.$t('catalog.menu.importFailed'))
      } finally {
        this.importing = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.menu-page {
  --ink: #122033;
  --muted: #6a7b90;
  --line: #d7e2ee;
  --accent: #0b4f8a;
  --accent-soft: #e7f1f8;
  --bg: #f4f7fb;
  max-width: 980px;
}

.menu-page__header {
  margin-bottom: 22px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 800;
    color: var(--ink);
  }

  p {
    margin: 6px 0 0;
    color: var(--muted);
    font-size: 14px;
  }
}

.menu-page__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.menu-card {
  padding: 22px;
  border: 1px solid var(--line);
  border-radius: 18px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 750;
    color: var(--ink);
  }

  p {
    margin: 0;
    color: var(--muted);
    font-size: 13.5px;
    line-height: 1.45;
  }
}

.menu-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: var(--accent-soft);
  color: var(--accent);

  .mdi {
    font-size: 22px;
  }

  &--import {
    background: #eef8f1;
    color: #1b7a45;
  }
}

.menu-card__btn {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-soft);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &--primary {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;

    &:hover:not(:disabled) {
      background: #093f6e;
      border-color: #093f6e;
      color: #fff;
    }
  }
}

.menu-card__file {
  display: block;
  cursor: pointer;

  input {
    display: none;
  }

  span {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    padding: 0 14px;
    border: 1px dashed var(--line);
    border-radius: 12px;
    background: var(--bg);
    color: var(--muted);
    font-size: 13.5px;
    font-weight: 650;
  }

  &:hover span {
    border-color: var(--accent);
    color: var(--accent);
  }
}

.menu-card__result {
  padding: 12px 14px;
  border-radius: 12px;
  background: #eef8f1;
  border: 1px solid #cfe8d8;

  p {
    margin: 0 0 6px;
    color: #1b7a45;
    font-weight: 750;
  }

  ul {
    margin: 0;
    padding-left: 18px;
    color: #1b7a45;
    font-size: 13px;
  }
}

.menu-page__example {
  margin-top: 18px;
  padding: 18px 20px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: #fff;

  h3 {
    margin: 0 0 10px;
    font-size: 15px;
    font-weight: 750;
    color: var(--ink);
  }

  pre {
    margin: 0;
    padding: 14px;
    border-radius: 12px;
    background: #0f172a;
    color: #e2e8f0;
    font-size: 12.5px;
    line-height: 1.45;
    overflow: auto;
  }
}

@media (max-width: 860px) {
  .menu-page__grid {
    grid-template-columns: 1fr;
  }
}
</style>
